import { useCallback, useEffect, useMemo, useState } from 'react';
import { OrderItem, OrderResponse } from '../../../api/orders';
import { UserProfile } from '../../../api/auth';
import { isServiceOrderItem } from '../../../utils/orders';

export type ServiceAssignmentValue = {
  id?: string;
  technician: UserProfile | null;
  bayId?: string | null;
  completed: boolean;
  completedAt: string | null;
  notes: string;
  startTime: string | null;
  endTime: string | null;
};

type ServiceAssignmentsMap = Record<string, ServiceAssignmentValue>;

type ServiceAssignmentPayloadEntry = {
  orderItemId: string;
  technicianId: string | null;
  completed: boolean;
  notes: string | null;
  completedAt?: string | null;
  startTime?: string | null;
  endTime?: string | null;
};

const createDefaultAssignment = (overrides: Partial<ServiceAssignmentValue> = {}): ServiceAssignmentValue => ({
  technician: null,
  completed: false,
  completedAt: null,
  notes: '',
  startTime: null,
  endTime: null,
  ...overrides,
});

const buildAssignmentsFromOrder = (order: OrderResponse | null): ServiceAssignmentsMap => {
  if (!order) {
    return {};
  }

  const map: ServiceAssignmentsMap = {};

  (order.items ?? []).forEach((item) => {
    if (!isServiceOrderItem(item)) {
      return;
    }

    const assignment = item.serviceTechnicians?.[0];
    if (assignment) {
      map[item.id] = createDefaultAssignment({
        id: assignment.id,
        technician: assignment.technician ?? null,
        bayId: assignment.bayId ?? null,
        completed: Boolean(assignment.completed),
        completedAt: assignment.completedAt ?? null,
        notes: assignment.notes ?? '',
        startTime: assignment.startTime ?? null,
        endTime: assignment.endTime ?? null,
      });
    } else {
      map[item.id] = createDefaultAssignment();
    }
  });

  return map;
};

export const useServiceAssignments = (order: OrderResponse | null) => {
  const [assignments, setAssignments] = useState<ServiceAssignmentsMap>(() => buildAssignmentsFromOrder(order));

  useEffect(() => {
    setAssignments(buildAssignmentsFromOrder(order));
  }, [order]);

  const serviceItems = useMemo<OrderItem[]>(
    () => (order?.items ?? []).filter(isServiceOrderItem),
    [order],
  );

  const dirty = useMemo(() => {
    if (!serviceItems.length) {
      return false;
    }

    return serviceItems.some((item) => {
      const current = assignments[item.id] ?? createDefaultAssignment();
      const initialAssignment = item.serviceTechnicians?.[0];
      const initialTechnicianId = initialAssignment?.technician?.id ?? null;
      const currentTechnicianId = current.technician?.id ?? null;
      const initialBayId = initialAssignment?.bayId ?? null;
      const currentBayId = current.bayId ?? null;
      const initialCompleted = Boolean(initialAssignment?.completed);
      const currentCompleted = Boolean(current.completed);
      const initialCompletedAt = initialAssignment?.completedAt ?? null;
      const currentCompletedAt = current.completedAt ?? null;
      const initialNotes = (initialAssignment?.notes ?? '').trim();
      const currentNotes = current.notes.trim();
      const initialStartTime = initialAssignment?.startTime ?? null;
      const currentStartTime = current.startTime ?? null;
      const initialEndTime = initialAssignment?.endTime ?? null;
      const currentEndTime = current.endTime ?? null;

      return (
        initialTechnicianId !== currentTechnicianId
        || initialBayId !== currentBayId
        || initialCompleted !== currentCompleted
        || (initialCompletedAt ?? null) !== (currentCompletedAt ?? null)
        || initialNotes !== currentNotes
        || initialStartTime !== currentStartTime
        || initialEndTime !== currentEndTime
      );
    });
  }, [serviceItems, assignments]);

  const payload = useMemo<ServiceAssignmentPayloadEntry[]>(
    () =>
      serviceItems.map((item) => {
        const current = assignments[item.id] ?? createDefaultAssignment();
        const technicianId = current.technician?.id ?? null;
        const notes = current.notes.trim();
        const completedAt = current.completed ? current.completedAt : null;

        const payloadEntry: ServiceAssignmentPayloadEntry = {
          orderItemId: item.id,
          technicianId,
          completed: Boolean(current.completed),
          notes: notes.length ? notes : null,
          startTime: current.startTime,
          endTime: current.endTime,
        };
        if (current.bayId !== undefined) {
          (payloadEntry as any).bayId = current.bayId ?? null;
        }

        if (!payloadEntry.completed) {
          payloadEntry.completedAt = null;
        } else if (completedAt) {
          payloadEntry.completedAt = completedAt;
        }

        return payloadEntry;
      }),
    [serviceItems, assignments],
  );

  type AssignmentUpdates =
    | Partial<ServiceAssignmentValue>
    | ((current: ServiceAssignmentValue) => Partial<ServiceAssignmentValue>);

  const updateAssignment = useCallback(
    (orderItemId: string, updates: AssignmentUpdates) => {
      setAssignments((prev) => {
        const current = prev[orderItemId] ?? createDefaultAssignment();
        const resolvedUpdates =
          typeof updates === 'function' ? updates(current) : updates;
        const next: ServiceAssignmentValue = {
          ...current,
          ...resolvedUpdates,
        };

        if ('technician' in resolvedUpdates && !resolvedUpdates.technician) {
          next.completed = false;
          next.notes = '';
          next.completedAt = null;
        }

        if (
          current.technician?.id === next.technician?.id
          && current.bayId === next.bayId
          && current.completed === next.completed
          && current.completedAt === next.completedAt
          && current.notes === next.notes
          && current.startTime === next.startTime
          && current.endTime === next.endTime
        ) {
          return prev;
        }

        return {
          ...prev,
          [orderItemId]: next,
        };
      });
    },
    [],
  );

  const setAssignmentTechnician = useCallback(
    (orderItemId: string, technician: UserProfile | null) => {
      updateAssignment(orderItemId, (current) => {
        if (!technician) {
          return {
            technician: null,
            completed: false,
            completedAt: null,
            notes: '',
          };
        }

        if (current.technician?.id === technician.id) {
          return { technician };
        }

        return {
          technician,
          completed: false,
          completedAt: null,
          notes: '',
        };
      });
    },
    [updateAssignment],
  );

  const setAssignmentCompleted = useCallback(
    (orderItemId: string, completed: boolean) => {
      setAssignments((prev) => {
        const current = prev[orderItemId] ?? createDefaultAssignment();
        if (!current.technician || !completed || current.completed) {
          return {
            ...prev,
            [orderItemId]: { ...current, completed, completedAt: (completed ? (current.completedAt ?? new Date().toISOString()) : null) },
          };
        }

        const actualEndTime = new Date();
        const scheduledEndTime = current.endTime ? new Date(current.endTime) : null;

        let timeSavedMs = 0;
        if (scheduledEndTime && actualEndTime < scheduledEndTime) {
          timeSavedMs = scheduledEndTime.getTime() - actualEndTime.getTime();
        }

        const nextAssignments = { ...prev };
        nextAssignments[orderItemId] = {
          ...current,
          completed: true,
          completedAt: actualEndTime.toISOString(),
          // endTime: actualEndTime.toISOString(), // Remove this line to keep EST time
        };

        // Pull forward subsequent jobs for the same technician or bay in this order
        if (timeSavedMs > 0) {
          Object.keys(nextAssignments).forEach((id) => {
            if (id === orderItemId) return;
            const target = nextAssignments[id];

            // Only pull forward if it's the same tech or bay AND it starts AFTER the completed task's original end time
            const isSameResource = (target.technician?.id === current.technician?.id) || (target.bayId === current.bayId);
            const startsAfter = scheduledEndTime && target.startTime && new Date(target.startTime) >= scheduledEndTime;

            if (isSameResource && startsAfter && target.startTime && target.endTime) {
              const newStart = new Date(new Date(target.startTime).getTime() - timeSavedMs);
              const newEnd = new Date(new Date(target.endTime).getTime() - timeSavedMs);
              nextAssignments[id] = {
                ...target,
                startTime: newStart.toISOString(),
                endTime: newEnd.toISOString(),
              };
            }
          });
        }

        return nextAssignments;
      });
    },
    [],
  );

  const setAssignmentNotes = useCallback(
    (orderItemId: string, notes: string) => {
      updateAssignment(orderItemId, { notes });
    },
    [updateAssignment],
  );

  return {
    assignments,
    updateAssignment,
    setAssignmentTechnician,
    setAssignmentCompleted,
    setAssignmentNotes,
    dirty,
    payload,
    serviceItems,
  } as const;
};

export type ServiceAssignmentsState = ReturnType<typeof useServiceAssignments>;
